import './EsRibbon.css';
import { useEsRibbonFlag } from '../../common/EsFlags';

/**
 * The announcement this run has, trimmed, or the empty string for none.
 *
 * <p>Exported because TWO components need the same answer: this one decides
 * whether to render, and EsParent decides whether to leave room for it. A
 * second `.trim()` in the shell would be a second rule about what counts as an
 * announcement, and the day they disagreed the page would reserve 36px for a
 * ribbon that is not there.
 *
 * <p>Trimmed before the test, so a field holding a space is off rather than a
 * ribbon with nothing written on it. Whitespace is what a value looks like
 * after somebody has cleared it in a hurry.
 */
export const useEsRibbonMessage = () => ( useEsRibbonFlag() || '' ).trim();

/**
 * The announcement ribbon: one line of text across the top of every page,
 * switched on and written by LaunchDarkly.
 *
 * ---------------------------------------------------------------------------
 * THE FLAG CARRIES THE MESSAGE, WHICH IS WHY THERE IS NO SECOND ONE
 * ---------------------------------------------------------------------------
 *
 * `switchEsRibbon` is a STRING flag and its value is the announcement. Empty
 * means no ribbon. A boolean flag beside a text field would be two things to
 * get right -- on with nothing to say, or something to say with the switch off
 * -- and one of those two states is always a bug somebody has to find. One
 * field cannot disagree with itself.
 *
 * The default is the empty string, which is the rule every flag here follows:
 * a default reproduces what the app does today, and today there is no ribbon.
 * So a LaunchDarkly outage, or a checkout with no client id, silently means no
 * announcement rather than a stale one nobody can take down.
 *
 * ---------------------------------------------------------------------------
 * WHY IT IS FIXED, AND WHAT MAKES ROOM FOR IT
 * ---------------------------------------------------------------------------
 *
 * EsNavBar is `absolute top-0 z-40` -- it floats over the top of the hero
 * rather than sitting in the flow. So a ribbon in normal flow at the top of
 * the shell would be rendered UNDER it and be invisible on the pages with a
 * hero, which is most of them. Hence `fixed top-0 z-50`.
 *
 * Fixed rather than sticky, too: an announcement that scrolls away has been
 * read by nobody who arrived halfway down a page.
 *
 * Fixed means out of the flow, so nothing moves out of its way on its own --
 * and it overlapped the nav band until 2026-09-17. `.esRibbonOffset` in
 * EsRibbon.css is what makes the room: EsParent wears it whenever there is a
 * message, and it pushes the shell's own children down by --es-ribbon-h and
 * moves the nav bar's `top` by the same amount, because the nav bar is
 * absolute and padding does not reach it. No announcement, no class, and the
 * page is exactly what it was.
 *
 * ---------------------------------------------------------------------------
 * ACCESSIBILITY
 * ---------------------------------------------------------------------------
 *
 * `role="status"` with `aria-live="polite"`, not `role="alert"`. It is an
 * announcement, not a failure: polite waits for a pause instead of cutting
 * across whatever the reader is in the middle of. It is announced when the
 * flag changes mid-session, which is exactly when somebody needs telling.
 */
export default function EsRibbon() {
    const aMessage = useEsRibbonMessage();

    if ( !aMessage ) return null;

    return (
        <div
            data-testid="es-ribbon"
            role="status"
            aria-live="polite"
            className="esRibbon fixed inset-s-0 z-50 w-full px-4 text-center text-sm"
        >
            { /* The tip is the whole message, for a ribbon whose
                 text is wider than the screen -- see .esRibbonText. */ }
            <span data-testid="es-ribbon-text" className="esRibbonText block" data-tip={ aMessage }>
                { aMessage }
            </span>
        </div>
    );
}
